import { createFileRoute } from "@tanstack/react-router"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useEffect } from "react"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.tsx"
import {
  CircleDollarSign,
  Info,
  Settings,
  ShieldLock,
  UserPen,
} from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { userApi } from "@/api/user.api.ts"
import { Card, CardContent } from "@/components/ui/card.tsx"
import { AccountSettings } from "@/components/settings/AccountSettings.tsx"
import { listsApi } from "@/api/lists.api.ts"
import { countryCodeToFlag } from "@/lib/utils.ts"
import { ProfileSettings } from "@/components/settings/ProfileSettings.tsx"
import { ProfessionalInfoSettings } from "@/components/settings/ProfessionalInfoSettings.tsx"
import { PaymentSettings } from "@/components/settings/PaymentSettings.tsx"
import { SecuritySettings } from "@/components/settings/SecuritySettings.tsx"

export const Route = createFileRoute("/_protected/account/settings")({
  component: RouteComponent,
})

function RouteComponent() {
  const { setTitle } = usePageTitle()
  useEffect(() => {
    setTitle("Account Settings")
  }, [setTitle])

  const { data, isLoading, error } = useQuery({
    queryKey: ["GetCurrentUserProfileForEdit"],
    queryFn: userApi.GetCurrentUserProfileForEdit,
    structuralSharing: false,
  })

  const { data: optionsData } = useQuery({
    queryKey: ["getSignupOptions"],
    queryFn: () => listsApi.getSignupOptions(),
    staleTime: 60 * 60 * 1000,
    gcTime: 120 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const formats =
    optionsData?.result?.formats.map((format) => ({
      id: format.id,
      name: format.nameKey,
    })) || []

  const themes =
    optionsData?.result?.themes.map((theme) => ({
      id: theme.id,
      name: theme.nameKey,
    })) || []

  const countries =
    optionsData?.result?.countries.map((country) => ({
      id: country.id,
      name: country.name,
    })) || []

  const countryCodes =
    optionsData?.result?.countries.map((country) => ({
      id: country.code,
      name: country.phoneCode,
      flag: countryCodeToFlag(country.code),
    })) || []

  return (
    <div>
      <Tabs defaultValue="account">
        <TabsList>
          <TabsTrigger value="account">
            <Settings /> Account
          </TabsTrigger>
          <TabsTrigger value="profile">
            <UserPen /> Profile
          </TabsTrigger>
          <TabsTrigger value="professionalInfo">
            <Info /> Professional Info
          </TabsTrigger>
          <TabsTrigger value="payment">
            <CircleDollarSign /> Payment Info
          </TabsTrigger>
          <TabsTrigger value="security">
            <ShieldLock /> Security
          </TabsTrigger>
        </TabsList>
        <Card className="mt-2 h-full bg-muted shadow-none ring-0">
          <CardContent>
            <TabsContent value="account">
              <AccountSettings countryCodes={countryCodes} />
            </TabsContent>
            <TabsContent value="profile">
              <ProfileSettings />
            </TabsContent>
            <TabsContent value="professionalInfo">
              <ProfessionalInfoSettings />
            </TabsContent>
            <TabsContent value="payment">
              <PaymentSettings />
            </TabsContent>
            <TabsContent value="security">
              <SecuritySettings />
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>
    </div>
  )
}
